import React, { ReactNode, useEffect, useRef, useState, useMemo } from "react";
import { View } from "@tarojs/components";
import classNames from "classnames";
import { pxTransform } from "@tarojs/taro";
import { Property } from "csstype";
import Icon from "../Icon";
import { XHComponentCommonProps } from "../../types";

import "./index.scss";

export type RemindProps = {
  content: string[] | ReactNode[];
  internal?: number; // 轮播间隔，毫秒
  duration?: number; // 动画时长，毫秒
  icon?: ReactNode | boolean; // 支持传入 ReactNode 或布尔值控制默认图标
  itemHeight?: number; // 单个轮播项的高度，px，默认为 72
  bgColor?: Property.BackgroundColor;
} & XHComponentCommonProps;

const Notice: React.FC<RemindProps> = props => {
  const {
    content: originalContent,
    internal = 3000,
    duration = 500,
    icon = false,
    itemHeight = 72, // 使用默认值 72
    className,
  } = props;

  // 使用 useMemo 缓存扩展内容数组，用于无缝轮播
  const content = useMemo(() => {
    return originalContent.length > 0
      ? [...originalContent, originalContent[0]]
      : [];
  }, [originalContent]);

  const internalRef = useRef<any>(null);
  const timeoutRef = useRef<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [transitionDuration, setTransitionDuration] = useState(duration);

  useEffect(() => {
    if (content.length <= 1) return; // 内容少于等于一项无需轮播

    if (internalRef.current) clearInterval(internalRef.current);

    internalRef.current = setInterval(() => {
      setCurrentIndex(prevIndex => {
        const nextIndex = prevIndex + 1;
        // 正常轮播到下一项，如果下一项是副本，会在下面的 useEffect 中处理瞬移
        return nextIndex;
      });
    }, internal);

    return () => {
      internalRef.current && clearInterval(internalRef.current);
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [content.length, internal]); // 依赖 content.length 和 internal

  // 处理到达副本项后的瞬移
  useEffect(() => {
    if (content.length <= 1) return; // 内容少于等于一项无需轮播

    if (currentIndex === content.length - 1 && transitionDuration !== 0) {
      // 当准备轮播到最后一个（副本前一个）时，设置一个延时，在动画即将完成时瞬间跳跃
      timeoutRef.current = setTimeout(() => {
        setTransitionDuration(0); // 关闭动画
        setCurrentIndex(0); // 瞬移到第一个
      }, duration); // 延时等于动画时长，等待动画完成再跳
    } else if (currentIndex === 0 && transitionDuration === 0) {
      // 当已经瞬移回第一个后，在一个极短的延迟后恢复动画时长
      timeoutRef.current = setTimeout(() => {
        setTransitionDuration(duration); // 恢复动画
      }, 50); // 极短延迟，确保样式已应用
    }

    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [currentIndex, content.length, duration, transitionDuration]);

  const cls = classNames("xh-notice", className);

  return (
    <View className={cls}>
      {typeof icon === "boolean" && icon ? (
        <Icon icon="notice" color="#7384F6" size={32} />
      ) : (
        icon
      )}
      <div
        className="xh-notice-main"
        style={{
          transform: `translateY(${pxTransform(-currentIndex * itemHeight)})`, // 使用 itemHeight
          transitionDuration: `${transitionDuration}ms`,
          transitionTimingFunction: "ease-in-out", // 可以根据需要调整缓动函数
        }}
      >
        {content.map((item, index) => (
          // key 使用 index 在这里是安全的，因为我们只是通过 index 来计算 transform
          // 即使内容相同，每个 View 元素在逻辑上是不同的轮播项（包括副本）
          <View className="xh-notice-main-marquee" key={index}>
            {item}
          </View>
        ))}
      </div>
    </View>
  );
};

export default Notice;
